import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Globe, Loader2, ArrowLeft, ArrowRight, CheckCircle2, Wand2,
  Sparkles, Palette, Layers, Download, Copy, RefreshCw, ImageIcon,
  Type, MessageSquare, LayoutGrid, Rocket, Info, X, Edit3, Check,
  Building2, Target, Lightbulb, Heart, Megaphone, FileJson,
  Coins, Facebook, Instagram, Share2, CalendarClock, Plus,
} from "lucide-react";
import { toast } from "sonner";
import DraftWatermark from "@/components/DraftWatermark";
import RightsChip from "@/components/RightsChip";
import RightsAttachModal from "@/components/RightsAttachModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _authHeaders = () => {
  const t = localStorage.getItem("wt_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEPS = [
  { key: "url",       label: "Website",   icon: Globe },
  { key: "brand",     label: "Brand",     icon: Building2 },
  { key: "objective", label: "Goal",      icon: Target },
  { key: "concept",   label: "Concept",   icon: Sparkles },
  { key: "generate",  label: "Posts",     icon: Layers },
  { key: "review",    label: "Approve",   icon: CheckCircle2 },
  { key: "export",    label: "Export",    icon: Download },
];

const CONCEPT_ICON = { educational: Lightbulb, emotional: Heart, promotional: Megaphone };

export default function VisualStudio() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [stepIdx, setStepIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);

  // ── State across the flow ────────────────────────────────────────────
  const [url, setUrl] = useState("");
  const [brand, setBrand] = useState(null);
  const [brandDraft, setBrandDraft] = useState({});     // user edits
  const [objective, setObjective] = useState("auto");
  const [campaign, setCampaign] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [posts, setPosts] = useState([]);
  const [credits, setCredits] = useState(null);

  const refreshCredits = async () => {
    if (!localStorage.getItem("wt_token")) return;
    try {
      const { data } = await axios.get(`${API}/visual-studio/credits`,
                                          { headers: _authHeaders() });
      setCredits(data);
    } catch {}
  };

  useEffect(() => {
    axios.get(`${API}/visual-studio/meta`).then(r => setMeta(r.data)).catch(() => {});
    refreshCredits();
    // Handle Meta OAuth callback flag from URL
    if (params.get("meta") === "connected" || params.get("meta") === "stub-connected") {
      toast.success("Meta connected");
    }
    // Handle Stripe topup success callback
    const topupSid = params.get("topup_session_id");
    if (topupSid) {
      (async () => {
        try {
          const { data } = await axios.get(
            `${API}/visual-studio/credits/topup/${topupSid}`,
            { headers: _authHeaders() });
          if (data.payment_status === "paid") {
            toast.success(`+${data.credits_added || 100} credits added!`);
          } else {
            toast.info("Payment processing — check back in a moment");
          }
          refreshCredits();
          setParams({}, { replace: true });
        } catch {}
      })();
    }
    if (params.get("topup_cancelled")) {
      toast.info("Top-up cancelled");
      setParams({}, { replace: true });
    }
    // Handle setup-intent (save card) success
    const setupSid = params.get("setup_session_id");
    if (setupSid) {
      (async () => {
        try {
          const { data } = await axios.get(
            `${API}/visual-studio/credits/setup-status/${setupSid}`,
            { headers: _authHeaders() });
          if (data.saved) {
            toast.success(`Card saved · ${data.card_brand} •••• ${data.card_last4} · auto-refill on`);
          }
          refreshCredits();
          setParams({}, { replace: true });
        } catch {}
      })();
    }
    if (params.get("setup_cancelled")) {
      toast.info("Card setup cancelled");
      setParams({}, { replace: true });
    }
  }, []);

  // ── Resume: hydrate from ?campaign=<id> if present ───────────────────
  useEffect(() => {
    const cid = params.get("campaign");
    if (!cid || campaign?.id === cid) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/visual-studio/campaigns/${cid}`);
        const c = data.campaign;
        setCampaign(c);
        setObjective(c.objective || "auto");
        setSelectedConcept(c.selected_concept);
        setPosts(c.posts || []);
        // Fetch its brand too
        if (c.brand_id) {
          const b = await axios.get(`${API}/visual-studio/brands/${c.brand_id}`);
          setBrand(b.data.brand);
          setBrandDraft({
            business_name: valueOf(b.data.brand.business_name),
            one_liner: valueOf(b.data.brand.one_liner),
            business_type: valueOf(b.data.brand.business_type),
            call_to_action: valueOf(b.data.brand.call_to_action),
            services: listOf(b.data.brand.services).join(", "),
            target_customers: listOf(b.data.brand.target_customers).join(", "),
          });
        }
        // Jump to the last step the user reached
        const stepMap = { concept: 3, posts: 4, review: 5, done: 6 };
        setStepIdx(stepMap[c.current_step] ?? 4);
        toast.success("Campaign resumed");
      } catch (err) {
        toast.error("Couldn't resume campaign");
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = STEPS[stepIdx];
  const go = (n) => setStepIdx(Math.max(0, Math.min(STEPS.length - 1, n)));

  // ─────────────────────────────────────────────────────────────────────
  //  Step 1 — Website URL → analyze
  // ─────────────────────────────────────────────────────────────────────
  const analyze = async () => {
    if (!url.trim()) return toast.error("Enter a website URL");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/visual-studio/analyze`, { url: url.trim() });
      setBrand(data.brand);
      setBrandDraft({
        business_name: valueOf(data.brand.business_name),
        one_liner: valueOf(data.brand.one_liner),
        business_type: valueOf(data.brand.business_type),
        call_to_action: valueOf(data.brand.call_to_action),
        services: listOf(data.brand.services).join(", "),
        target_customers: listOf(data.brand.target_customers).join(", "),
      });
      go(1);
      toast.success(data.cached ? "Loaded saved brand" : "Brand analyzed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analyze failed");
    } finally { setBusy(false); }
  };

  // ─────────────────────────────────────────────────────────────────────
  //  Step 2 — Confirm brand
  // ─────────────────────────────────────────────────────────────────────
  const saveBrand = async () => {
    if (!brand?.id) return;
    setBusy(true);
    try {
      const fields = {
        business_name: brandDraft.business_name,
        one_liner: brandDraft.one_liner,
        business_type: brandDraft.business_type,
        call_to_action: brandDraft.call_to_action,
        services: brandDraft.services.split(",").map(s => s.trim()).filter(Boolean),
        target_customers: brandDraft.target_customers.split(",").map(s => s.trim()).filter(Boolean),
      };
      const { data } = await axios.put(`${API}/visual-studio/brands/${brand.id}`, { fields });
      setBrand(data.brand);
      go(2);
    } catch (err) {
      toast.error("Couldn't save brand");
    } finally { setBusy(false); }
  };

  // ─────────────────────────────────────────────────────────────────────
  //  Step 3 → Step 4: create campaign → 3 concepts
  // ─────────────────────────────────────────────────────────────────────
  const createCampaign = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/visual-studio/campaigns`, {
        brand_id: brand.id, objective,
      }, { headers: _authHeaders() });
      setCampaign(data.campaign);
      setParams({ campaign: data.campaign.id }, { replace: true });
      refreshCredits();
      go(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Concept generation failed");
    } finally { setBusy(false); }
  };

  const generatePosts = async (conceptKey) => {
    if (!campaign?.id) return;
    setSelectedConcept(conceptKey);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/campaigns/${campaign.id}/generate`,
        { concept_key: conceptKey, post_count: 4 },
        { headers: _authHeaders() }
      );
      setPosts(data.posts);
      go(4);
      refreshCredits();
      // Fire off default renders in the background
      data.posts.forEach(p => renderPost(p.post_id, p.template_id, "square"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post generation failed");
    } finally { setBusy(false); }
  };

  const renderPost = async (postId, templateId, size = "square") => {
    try {
      const { data } = await axios.post(`${API}/visual-studio/render`, {
        post_id: postId, template_id: templateId, size,
      });
      setPosts(prev => prev.map(p => p.post_id === postId ? {
        ...p, renders: { ...(p.renders || {}), [size]: data.render },
        template_id: data.render.template_id,
      } : p));
    } catch {}
  };

  const regenerate = async (postId, component, newValue = null) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/visual-studio/regenerate`, {
        post_id: postId, component, new_value: newValue,
      }, { headers: _authHeaders() });
      setPosts(prev => prev.map(p => p.post_id === postId ? data.post : p));
      // Re-render after change
      renderPost(postId, data.post.template_id, "square");
      refreshCredits();
      toast.success(`${component} regenerated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regen failed");
    } finally { setBusy(false); }
  };

  const approve = async (postId, approved = true) => {
    try {
      await axios.post(`${API}/visual-studio/approve`, { post_id: postId, approved });
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, approved } : p));
    } catch {}
  };

  const exportZip = async () => {
    if (!campaign?.id) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/visual-studio/campaigns/${campaign.id}/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${brandDraft.business_name || "campaign"}.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Campaign exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  const approvedCount = posts.filter(p => p.approved).length;

  // ─────────────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100" data-testid="visual-studio-page">
      <TopNav credits={credits} />
      <ProgressBar stepIdx={stepIdx} onJump={go} unlocked={{
        1: !!brand, 2: !!brand, 3: !!campaign, 4: posts.length > 0,
        5: posts.length > 0, 6: approvedCount > 0,
      }} />

      <div className="max-w-6xl mx-auto px-6 py-10">
        {step.key === "url" && (
          <UrlScreen url={url} setUrl={setUrl} onNext={analyze} busy={busy} />
        )}
        {step.key === "brand" && brand && (
          <BrandScreen brand={brand} draft={brandDraft} setDraft={setBrandDraft}
                        onBack={() => go(0)} onNext={saveBrand} busy={busy} />
        )}
        {step.key === "objective" && meta && (
          <ObjectiveScreen meta={meta} value={objective} onChange={setObjective}
                            onBack={() => go(1)} onNext={createCampaign} busy={busy} />
        )}
        {step.key === "concept" && campaign && (
          <ConceptScreen campaign={campaign} onPick={generatePosts}
                          onBack={() => go(2)} busy={busy} />
        )}
        {step.key === "generate" && (
          <PostsScreen posts={posts} meta={meta}
                        onRerender={renderPost}
                        onRegenerate={regenerate} onApprove={approve}
                        onBack={() => go(3)} onNext={() => go(5)} busy={busy} />
        )}
        {step.key === "review" && (
          <ReviewScreen posts={posts} onApprove={approve}
                          onBack={() => go(4)} onNext={() => go(6)}
                          onRegenerate={regenerate}
                          approvedCount={approvedCount} />
        )}
        {step.key === "export" && (
          <ExportScreen posts={posts.filter(p => p.approved)}
                          campaignId={campaign?.id}
                          onExport={exportZip} busy={busy}
                          onBack={() => go(5)} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Small building blocks
// ─────────────────────────────────────────────────────────────────────────
function valueOf(v) { if (v && typeof v === "object" && "value" in v) return v.value || ""; return v || ""; }
function listOf(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object" && Array.isArray(v.value)) return v.value;
  return [];
}

function TopNav({ credits }) {
  const [tipOpen, setTipOpen] = useState(false);
  const [autoRefill, setAutoRefill] = useState(null);

  useEffect(() => {
    if (!tipOpen || !localStorage.getItem("wt_token")) return;
    axios.get(`${API}/visual-studio/credits/auto-refill`,
                { headers: _authHeaders() })
          .then(r => setAutoRefill(r.data)).catch(() => {});
  }, [tipOpen]);

  const buyCredits = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/credits/checkout`,
        { origin_url: window.location.origin },
        { headers: _authHeaders() });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed");
    }
  };

  const setupAutoRefill = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/credits/setup-checkout`,
        { origin_url: window.location.origin },
        { headers: _authHeaders() });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup failed");
    }
  };

  const toggleAutoRefill = async (e) => {
    e.stopPropagation();
    if (!autoRefill?.has_saved_card) return setupAutoRefill(e);
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/credits/auto-refill`,
        { enabled: !autoRefill.enabled,
           threshold: autoRefill.threshold,
           monthly_cap_cents: autoRefill.monthly_cap_cents },
        { headers: _authHeaders() });
      setAutoRefill(r => ({ ...r, ...data.auto_refill }));
      toast.success(data.auto_refill.enabled ? "Auto-refill on" : "Auto-refill off");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <header className="border-b border-white/5 bg-[#0b1120]/95 sticky top-0 z-30 backdrop-blur">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 text-slate-100 hover:text-white transition"
            data-testid="vs-home-link">
          <Wand2 className="w-5 h-5 text-fuchsia-400" />
          <span className="font-semibold tracking-tight">Visual Studio</span>
          <span className="text-xs text-slate-500 ml-2">·  Pilot</span>
        </a>
        <div className="flex items-center gap-3 text-sm">
          {credits?.balance != null && (
            <div className="relative"
                  onMouseEnter={() => setTipOpen(true)}
                  onMouseLeave={() => setTipOpen(false)}>
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full
                                text-xs font-mono cursor-help border
                                ${credits.balance < 10
                                    ? "bg-red-500/10 text-red-300 border-red-500/40"
                                    : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}
                    data-testid="vs-credits-chip">
                <Coins className="w-3.5 h-3.5" /> {credits.balance}
                {autoRefill?.enabled && (
                  <Rocket className="w-3 h-3 text-emerald-400" data-testid="vs-autorefill-on" />
                )}
              </div>
              {tipOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-slate-900
                                    border border-white/10 rounded-xl p-3 shadow-xl z-40"
                      data-testid="vs-credits-tooltip">
                  <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider">
                    Recent spend
                  </div>
                  {(credits.recent || []).slice(0, 5).length === 0 ? (
                    <div className="text-xs text-slate-500">Nothing spent yet</div>
                  ) : (
                    <div className="space-y-1">
                      {(credits.recent || []).slice(0, 5).map((e, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-slate-300 capitalize">{e.kind.replace(/_/g, " ")}</span>
                          <span className={`font-mono ${e.amount < 0 ? "text-emerald-300" : "text-amber-300"}`}>
                            {e.amount < 0 ? "+" : "-"}{Math.abs(e.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 pt-2 border-t border-white/5 text-[10px] text-slate-500">
                    Total spent: {credits.total_spent} · Start: {credits.starting_balance}
                  </div>
                  <button onClick={buyCredits} data-testid="vs-topup-btn"
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5
                                      px-3 py-2 rounded-lg bg-gradient-to-tr
                                      from-fuchsia-500 to-violet-500 text-white
                                      text-xs font-medium hover:shadow-lg hover:shadow-fuchsia-500/40
                                      transition">
                    <Coins className="w-3 h-3" /> Add 100 credits — $5
                  </button>
                  {/* Auto-refill row */}
                  <div className="mt-2 pt-2 border-t border-white/5">
                    {autoRefill ? (
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-xs font-medium text-slate-300 flex items-center gap-1">
                            <Rocket className="w-3 h-3" /> Auto-refill
                            {autoRefill.enabled && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200">ON</span>
                            )}
                          </div>
                          {autoRefill.has_saved_card ? (
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {autoRefill.card_brand} •••• {autoRefill.card_last4} · cap ${(autoRefill.monthly_cap_cents/100).toFixed(0)}/mo
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              Save a card to never hit zero
                            </div>
                          )}
                        </div>
                        <button onClick={toggleAutoRefill}
                                  data-testid="vs-autorefill-toggle"
                                  className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-200">
                          {autoRefill.has_saved_card ? (autoRefill.enabled ? "Turn off" : "Turn on") : "Save card"}
                        </button>
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-500">Loading…</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <a href="/my-campaigns" className="text-slate-400 hover:text-slate-200"
              data-testid="vs-mycampaigns-link">My Campaigns</a>
          <a href="/ad-builder/faq" className="text-slate-400 hover:text-slate-200"
              data-testid="vs-faq-link">Help</a>
        </div>
      </div>
    </header>
  );
}

function ProgressBar({ stepIdx, onJump, unlocked }) {
  return (
    <div className="border-b border-white/5 bg-slate-950/40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-2 overflow-x-auto">
        {STEPS.map((s, i) => {
          const done = i < stepIdx;
          const active = i === stepIdx;
          const canJump = i === 0 || unlocked[i];
          const Icon = s.icon;
          return (
            <button key={s.key} data-testid={`vs-step-${s.key}`}
                     disabled={!canJump}
                     onClick={() => canJump && onJump(i)}
                     className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition
                          ${active ? "bg-fuchsia-500/20 text-fuchsia-200 ring-1 ring-fuchsia-500/40"
                                   : done ? "bg-emerald-500/10 text-emerald-300"
                                          : "bg-white/5 text-slate-500"}
                          ${canJump ? "hover:bg-white/10" : "cursor-not-allowed"}`}>
              <Icon className="w-3.5 h-3.5" />
              <span>{i + 1}. {s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white/[0.03] border border-white/10 rounded-2xl p-6 ${className}`}>{children}</div>;
}

function PrimaryBtn({ children, onClick, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                  bg-gradient-to-tr from-fuchsia-500 to-violet-500
                  text-white font-medium shadow-lg shadow-fuchsia-500/20
                  hover:shadow-fuchsia-500/40 hover:-translate-y-0.5
                  transition disabled:opacity-40 disabled:cursor-not-allowed">
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl
                  bg-white/5 border border-white/10 text-slate-300
                  hover:bg-white/10 transition disabled:opacity-40">
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 1  Enter URL
// ─────────────────────────────────────────────────────────────────────────
function UrlScreen({ url, setUrl, onNext, busy }) {
  return (
    <div className="max-w-2xl mx-auto space-y-6 py-10">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/10 text-fuchsia-200 text-xs">
          <Sparkles className="w-3.5 h-3.5" /> Pilot · Turn websites into campaigns
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Enter your <span className="bg-gradient-to-r from-fuchsia-400 to-violet-400 bg-clip-text text-transparent">website</span>
        </h1>
        <p className="text-slate-400">We'll analyse it, understand your brand, and draft your campaign in minutes.</p>
      </div>
      <Card>
        <label className="text-xs text-slate-400 uppercase tracking-wider">Website URL</label>
        <input value={url} onChange={e => setUrl(e.target.value)}
                data-testid="vs-url-input"
                onKeyDown={e => e.key === "Enter" && onNext()}
                placeholder="https://indianfamilytree.com"
                className="w-full mt-2 px-4 py-3 rounded-xl bg-black/40 border border-white/10
                            focus:border-fuchsia-500/60 outline-none text-lg" />
        <div className="mt-4 flex justify-end">
          <PrimaryBtn onClick={onNext} disabled={busy} testId="vs-analyze-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
                     : <>Create my campaign <ArrowRight className="w-4 h-4" /></>}
          </PrimaryBtn>
        </div>
      </Card>
      <div className="text-xs text-slate-500 text-center">
        Your Brand Brain is saved — re-analysing the same site is instant next time.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 2  Confirm brand
// ─────────────────────────────────────────────────────────────────────────
function BrandScreen({ brand, draft, setDraft, onBack, onNext, busy }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const logo = listOf(brand.logo_candidates)[0];
  const colors = listOf(brand.colors);
  const [members, setMembers] = useState(brand.members || []);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("editor");
  const [autoDraft, setAutoDraft] = useState(!!brand.auto_draft);
  const [savingShare, setSavingShare] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [myRole, setMyRole] = useState(null);

  useEffect(() => {
    axios.get(`${API}/visual-studio/brands/${brand.id}/members`,
                 { headers: _authHeaders() })
          .then(r => { setMembers(r.data.members || []); setMyRole(r.data.my_role); })
          .catch(() => {});
  }, [brand.id]);

  const canManage = myRole === "owner" || !myRole;   // no role = owner-to-be

  const addMember = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if (members.some(m => m.email === e)) return toast.error("Already added");
    setMembers(m => [...m, { email: e, role: newRole }]);
    setNewEmail("");
  };
  const removeMember = (email) => setMembers(m => m.filter(x => x.email !== email));
  const changeRole = (email, role) => setMembers(m =>
    m.map(x => x.email === email ? { ...x, role } : x));

  const saveShare = async () => {
    setSavingShare(true);
    try {
      await axios.put(`${API}/visual-studio/brands/${brand.id}/members`,
                          { members: members.filter(m => m.role !== "owner") },
                          { headers: _authHeaders() });
      toast.success("Team saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in required to share");
    } finally { setSavingShare(false); }
  };

  const toggleAutoDraft = async () => {
    const next = !autoDraft;
    setSavingAuto(true);
    try {
      await axios.post(`${API}/visual-studio/brands/${brand.id}/auto-draft`,
                          { enabled: next, objective: "auto",
                            concept_key: "educational" },
                          { headers: _authHeaders() });
      setAutoDraft(next);
      toast.success(next ? "Weekly auto-draft enabled" : "Weekly auto-draft disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in required");
    } finally { setSavingAuto(false); }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">Confirm your brand</h2>
          <p className="text-sm text-slate-400 mt-1">
            We extracted these from your site. Edit anything that isn't quite right.
          </p>
        </div>
        <div className="text-xs text-slate-500">Step 2 of 7</div>
      </header>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-1 space-y-4">
          {logo && (
            <div className="aspect-square rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
              <img src={logo} alt="logo" className="max-w-[70%] max-h-[70%] object-contain" />
            </div>
          )}
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Brand colors</div>
            <div className="flex gap-2 flex-wrap">
              {colors.length > 0 ? colors.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-black/40 rounded-lg px-2 py-1 text-xs">
                  <span className="w-4 h-4 rounded" style={{ background: c }} />
                  <span className="font-mono">{c}</span>
                </div>
              )) : <span className="text-xs text-slate-500">Not detected</span>}
            </div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1">
            <Info className="w-3 h-3" /> Confirming saves this as your reusable Brand Brain
          </div>
        </Card>

        <Card className="md:col-span-2 space-y-4">
          <Field label="Business name" value={draft.business_name} onChange={v => set("business_name", v)}
                  testId="vs-brand-name" />
          <Field label="One-liner" value={draft.one_liner} onChange={v => set("one_liner", v)}
                  testId="vs-brand-oneliner" multiline />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Business type" value={draft.business_type} onChange={v => set("business_type", v)}
                    testId="vs-brand-type" />
            <Field label="Primary CTA" value={draft.call_to_action} onChange={v => set("call_to_action", v)}
                    testId="vs-brand-cta" />
          </div>
          <Field label="Services / topics (comma-separated)" value={draft.services}
                  onChange={v => set("services", v)} testId="vs-brand-services" />
          <Field label="Target customers (comma-separated)" value={draft.target_customers}
                  onChange={v => set("target_customers", v)} testId="vs-brand-audience" />
        </Card>
      </div>

      {/* Team members + Auto-draft controls */}
      <Card className="space-y-4">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="flex-1 min-w-[320px]">
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-4 h-4 text-fuchsia-300" />
              <label className="text-xs uppercase tracking-wider text-slate-300">
                Team members
              </label>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Owners manage the brand · Editors draft &amp; approve · Viewers see approved posts only.
            </p>
            {/* Existing members list */}
            <div className="space-y-1.5 mb-3">
              {members.map(m => (
                <div key={m.email} className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-1.5"
                       data-testid={`vs-member-${m.email}`}>
                  <div className="flex-1 text-xs truncate">{m.email}</div>
                  {m.role === "owner" ? (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200">Owner</span>
                  ) : (
                    <>
                      <select value={m.role}
                                 onChange={e => changeRole(m.email, e.target.value)}
                                 disabled={!canManage}
                                 data-testid={`vs-member-role-${m.email}`}
                                 className="text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-slate-200">
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      {canManage && (
                        <button onClick={() => removeMember(m.email)}
                                  className="text-slate-500 hover:text-red-300">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Add new */}
            {canManage && (
              <>
                <div className="flex gap-2 mb-2">
                  <input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                          data-testid="vs-share-emails"
                          placeholder="teammate@company.com"
                          onKeyDown={e => e.key === "Enter" && addMember()}
                          className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10
                                      focus:border-fuchsia-500/60 outline-none text-sm" />
                  <select value={newRole} onChange={e => setNewRole(e.target.value)}
                            data-testid="vs-share-role"
                            className="px-2 rounded-lg bg-black/40 border border-white/10 text-slate-200 text-sm">
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button onClick={addMember}
                            data-testid="vs-share-add"
                            className="px-3 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 text-sm">
                    Add
                  </button>
                </div>
                <button onClick={saveShare} disabled={savingShare}
                          data-testid="vs-share-save"
                          className="w-full px-4 py-2 rounded-lg bg-fuchsia-500/20 text-fuchsia-200
                                      hover:bg-fuchsia-500/30 text-sm disabled:opacity-50">
                  {savingShare ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save team"}
                </button>
              </>
            )}
            {!canManage && (
              <div className="text-[11px] text-slate-500">
                You are a <span className="capitalize font-medium">{myRole}</span> on this brand.
                Ask the owner to change your role.
              </div>
            )}
          </div>

          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-emerald-300" />
              <label className="text-xs uppercase tracking-wider text-slate-300">
                Weekly auto-draft
              </label>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Every Monday, we draft a fresh campaign for this brand — ready when you log in.
              Costs 3 credits per draft.
            </p>
            <button onClick={toggleAutoDraft} disabled={savingAuto || !canManage}
                    data-testid="vs-autodraft-toggle"
                    className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition
                       ${autoDraft ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                                       : "bg-white/5 text-slate-300 hover:bg-white/10"}
                       ${!canManage ? "opacity-50 cursor-not-allowed" : ""}`}>
              {savingAuto ? <Loader2 className="w-4 h-4 animate-spin" />
                             : autoDraft ? <><CheckCircle2 className="w-4 h-4" /> Enabled</>
                                             : <><Plus className="w-4 h-4" /> Enable</>}
            </button>
          </div>
        </div>
      </Card>

      <div className="flex justify-between">
        <GhostBtn onClick={onBack} testId="vs-brand-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </GhostBtn>
        <PrimaryBtn onClick={onNext} disabled={busy} testId="vs-brand-confirm">
          Confirm & continue <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, multiline }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      {multiline ? (
        <textarea value={value || ""} onChange={e => onChange(e.target.value)}
          data-testid={testId} rows={2}
          className="w-full mt-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10
                      focus:border-fuchsia-500/60 outline-none text-sm" />
      ) : (
        <input value={value || ""} onChange={e => onChange(e.target.value)}
          data-testid={testId}
          className="w-full mt-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10
                      focus:border-fuchsia-500/60 outline-none text-sm" />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 3  Objective
// ─────────────────────────────────────────────────────────────────────────
function ObjectiveScreen({ meta, value, onChange, onBack, onNext, busy }) {
  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold">What's the goal of this campaign?</h2>
        <p className="text-sm text-slate-400 mt-1">Pick one — we'll shape three concepts around it.</p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(meta?.objectives || []).map(o => (
          <button key={o.key} onClick={() => onChange(o.key)}
                    data-testid={`vs-objective-${o.key}`}
                    className={`text-left p-5 rounded-2xl border transition
                        ${value === o.key
                            ? "bg-fuchsia-500/10 border-fuchsia-500/60"
                            : "bg-white/[0.03] border-white/10 hover:border-white/30"}`}>
            <div className="font-medium">{o.label}</div>
          </button>
        ))}
      </div>
      <div className="flex justify-between">
        <GhostBtn onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</GhostBtn>
        <PrimaryBtn onClick={onNext} disabled={busy} testId="vs-obj-next">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                     : <>Generate concepts <Sparkles className="w-4 h-4" /></>}
        </PrimaryBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 4  Concept picker
// ─────────────────────────────────────────────────────────────────────────
function ConceptScreen({ campaign, onPick, onBack, busy }) {
  const entries = Object.entries(campaign.concepts || {});
  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold">Pick a campaign concept</h2>
        <p className="text-sm text-slate-400 mt-1">
          Three angles. Choose one and we'll draft the posts. You can always come back.
        </p>
      </header>
      <div className="grid md:grid-cols-3 gap-5">
        {entries.map(([key, c]) => {
          const Icon = CONCEPT_ICON[key] || Sparkles;
          return (
            <button key={key} onClick={() => onPick(key)} disabled={busy}
                data-testid={`vs-concept-${key}`}
                className="text-left p-6 rounded-2xl bg-white/[0.03] border border-white/10
                            hover:border-fuchsia-500/60 hover:bg-white/[0.06] transition group">
              <div className="flex items-center gap-2 text-fuchsia-300 mb-4">
                <Icon className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">{c.label}</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{c.headline}</h3>
              <p className="text-sm text-slate-400 mb-4">{c.description}</p>
              <div className="text-xs text-slate-500 space-y-1">
                <div className="font-medium text-slate-400 uppercase tracking-wider">Suggested posts</div>
                {(c.suggested_posts || []).slice(0, 4).map((p, i) => (
                  <div key={i} className="text-slate-400">• {p.topic || p.template_id.replace(/_/g, " ")}</div>
                ))}
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-fuchsia-300 text-sm group-hover:gap-3 transition-all">
                Choose this <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex justify-start">
        <GhostBtn onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</GhostBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 5  Post preview + component regen
// ─────────────────────────────────────────────────────────────────────────
function PostsScreen({ posts, meta, onRerender, onRegenerate, onApprove, onBack, onNext, busy }) {
  const [openPost, setOpenPost] = useState(null);
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Your posts</h2>
          <p className="text-sm text-slate-400 mt-1">
            {posts.length} posts drafted. Regenerate any single component — or approve to lock it in.
          </p>
        </div>
      </header>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map(p => (
          <PostCard key={p.post_id} post={p} meta={meta}
                     onRerender={onRerender}
                     onRegenerate={onRegenerate}
                     onApprove={onApprove}
                     onOpen={() => setOpenPost(p.post_id)} />
        ))}
      </div>
      <div className="flex justify-between">
        <GhostBtn onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</GhostBtn>
        <PrimaryBtn onClick={onNext} testId="vs-posts-next">
          Review approvals <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
    </div>
  );
}

function PostCard({ post, meta, onRerender, onRegenerate, onApprove, onOpen }) {
  const preview = post.renders?.square;
  const templateLabel = (meta?.templates || []).find(t => t.key === post.template_id)?.label
                            || post.template_id;
  const [showTemplates, setShowTemplates] = useState(false);
  const [rightsOpen, setRightsOpen] = useState(false);
  const rc = post.regen_counts || {};
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
          data-testid={`vs-post-${post.post_id}`}>
      <div className="aspect-square bg-black/40 relative overflow-hidden">
        {preview ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${preview.url}`}
                alt={post.alt_text} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {/* Draft watermark on every un-approved preview — never ships
            to export because exports strip it (approved=true at that
            point). */}
        <DraftWatermark show={!!preview && !post.approved} />
        {post.approved && (
          <div className="absolute top-3 right-3 bg-emerald-500/90 text-black rounded-full p-1.5 shadow-lg">
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
        {preview?.origin && (
          <div className="absolute bottom-2 left-2">
            <RightsChip
              origin={preview.origin}
              attached={!!preview?.rights}
              onClick={() => setRightsOpen(true)}
            />
          </div>
        )}
      </div>
      <RightsAttachModal
        open={rightsOpen}
        onClose={() => setRightsOpen(false)}
        postId={post.post_id}
        size="square"
        origin={preview?.origin}
        current={preview?.rights}
      />
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-fuchsia-300 mb-1">
            {templateLabel}
          </div>
          <div className="font-semibold text-sm line-clamp-2">{post.headline}</div>
          <p className="text-xs text-slate-400 mt-1 line-clamp-3">{post.caption}</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {(post.hashtags || []).slice(0, 4).map((h, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{h}</span>
          ))}
        </div>
        <div className="pt-3 border-t border-white/5 mt-auto">
          <div className="grid grid-cols-2 gap-1.5 text-[11px]">
            <MiniBtn testId={`vs-regen-headline-${post.post_id}`}
                       onClick={() => onRegenerate(post.post_id, "headline")}
                       icon={Type} count={rc.headline}>Headline</MiniBtn>
            <MiniBtn testId={`vs-regen-caption-${post.post_id}`}
                       onClick={() => onRegenerate(post.post_id, "caption")}
                       icon={MessageSquare} count={rc.caption}>Caption</MiniBtn>
            <MiniBtn testId={`vs-regen-image-${post.post_id}`}
                       onClick={() => onRegenerate(post.post_id, "image")}
                       icon={ImageIcon} count={rc.image}>Image</MiniBtn>
            <MiniBtn testId={`vs-regen-template-${post.post_id}`}
                       onClick={() => setShowTemplates(v => !v)}
                       icon={LayoutGrid} count={rc.template}>Template</MiniBtn>
          </div>
          {(rc.headline || rc.caption || rc.image || rc.template) ? (
            <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
              <Info className="w-2.5 h-2.5" />
              {Object.entries(rc).reduce((s, [_, v]) => s + (v || 0), 0)} regenerations on this post
            </div>
          ) : null}
          {showTemplates && (
            <div className="mt-2 grid grid-cols-2 gap-1 p-2 bg-black/30 rounded-lg">
              {(meta?.templates || []).map(t => (
                <button key={t.key} onClick={() => {
                  onRegenerate(post.post_id, "template", t.key);
                  setShowTemplates(false);
                }} className={`text-[10px] px-2 py-1 rounded transition
                        ${t.key === post.template_id ? "bg-fuchsia-500/30 text-fuchsia-200"
                                                       : "hover:bg-white/10 text-slate-300"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <div className="mt-2 flex gap-1.5">
            <button onClick={() => onApprove(post.post_id, !post.approved)}
                     data-testid={`vs-approve-${post.post_id}`}
                     className={`flex-1 text-[11px] py-1.5 rounded transition font-medium
                        ${post.approved ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                                            : "bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/30"}`}>
              {post.approved ? "✓ Approved" : "Approve"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniBtn({ children, onClick, icon: Icon, testId, count }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="flex items-center gap-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-300
                  relative">
      <Icon className="w-3 h-3" /> {children}
      {count > 0 && (
        <span className="ml-auto text-[9px] font-mono bg-fuchsia-500/30 text-fuchsia-200
                             px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center"
                data-testid={`${testId}-count`}>
          {count}×
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 6  Review
// ─────────────────────────────────────────────────────────────────────────
function ReviewScreen({ posts, onApprove, onRegenerate, onBack, onNext, approvedCount }) {
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Review & approve</h2>
          <p className="text-sm text-slate-400 mt-1">
            {approvedCount} of {posts.length} approved — ready when you are.
          </p>
        </div>
      </header>
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.post_id} className="flex gap-4 items-center bg-white/[0.03] border border-white/10 rounded-xl p-3"
                 data-testid={`vs-review-${p.post_id}`}>
            <div className="w-20 h-20 bg-black/40 rounded-lg overflow-hidden flex-shrink-0 relative">
              {p.renders?.square?.url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.renders.square.url}`}
                       alt="" className="w-full h-full object-cover" />
              ) : null}
              <DraftWatermark show={!!p.renders?.square?.url && !p.approved} label="DRAFT" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{p.headline}</div>
              <div className="text-xs text-slate-400 mt-1 line-clamp-2">{p.caption}</div>
              {p.renders?.square?.origin && (
                <div className="mt-1.5"><RightsChip origin={p.renders.square.origin} /></div>
              )}
            </div>
            <button onClick={() => onApprove(p.post_id, !p.approved)}
                    data-testid={`vs-review-approve-${p.post_id}`}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition
                       ${p.approved ? "bg-emerald-500/20 text-emerald-200"
                                        : "bg-white/5 hover:bg-fuchsia-500/20 text-slate-300"}`}>
              {p.approved ? "✓ Approved" : "Approve"}
            </button>
          </div>
        ))}
      </div>
      <div className="flex justify-between">
        <GhostBtn onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</GhostBtn>
        <PrimaryBtn onClick={onNext} disabled={approvedCount === 0} testId="vs-review-next">
          Export {approvedCount} post{approvedCount === 1 ? "" : "s"} <ArrowRight className="w-4 h-4" />
        </PrimaryBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Screen · 7  Export
// ─────────────────────────────────────────────────────────────────────────
function ExportScreen({ posts, campaignId, onExport, busy, onBack }) {
  const [metaStatus, setMetaStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [shipping, setShipping] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("wt_token")) return;
    axios.get(`${API}/visual-studio/meta/status`, { headers: _authHeaders() })
          .then(r => setMetaStatus(r.data)).catch(() => {});
  }, []);

  const copy = async (t, label) => {
    try { await navigator.clipboard.writeText(t); toast.success(`${label} copied`); } catch {}
  };
  const downloadManifest = async () => {
    if (!campaignId) return;
    try {
      const res = await axios.get(
        `${API}/visual-studio/campaigns/${campaignId}/meta-manifest`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `campaign-${campaignId.slice(0, 8)}-meta.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Meta manifest downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Manifest download failed");
    }
  };
  const connectMeta = async () => {
    try {
      const { data } = await axios.get(`${API}/visual-studio/meta/connect`,
                                            { headers: _authHeaders() });
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        toast.success("Meta connected (stub mode)");
        // Refresh status
        const { data: s } = await axios.get(`${API}/visual-studio/meta/status`,
                                                { headers: _authHeaders() });
        setMetaStatus(s);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Meta connect failed");
    }
  };
  const publishMeta = async () => {
    if (!campaignId) return;
    setPublishing(true);
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/meta/publish/${campaignId}`,
        { targets: ["facebook", "instagram", "story"] },
        { headers: _authHeaders() });
      toast.success(`Published ${data.published} post${data.published === 1 ? "" : "s"} (${data.mode})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const shipToSocialPulse = async () => {
    if (!campaignId) return;
    setShipping(true);
    try {
      const { data } = await axios.post(
        `${API}/visual-studio/campaigns/${campaignId}/handoff-socialpulse`,
        {},
        { headers: _authHeaders() });
      setHandoff(data);
      // Immediately POST to the stub ack endpoint so the user sees a confirmed campaign/report id.
      try {
        const { data: ack } = await axios.post(`${API}/socialpulse/ack`, { token: data.token });
        setHandoff((h) => ({ ...h, ack: ack.ack }));
        toast.success(`Shipped to SocialPulse — ${ack.ack?.socialpulse_campaign_id}`);
      } catch (ackErr) {
        toast.info("Handoff token minted. SocialPulse ack pending.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SocialPulse handoff failed");
    } finally { setShipping(false); }
  };

  const connected = !!metaStatus?.connected;
  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold">Export your campaign</h2>
        <p className="text-sm text-slate-400 mt-1">
          Download all sizes + copy in one ZIP, or grab pieces below.
        </p>
      </header>
      <div className="flex flex-wrap justify-center gap-3 py-6">
        <PrimaryBtn onClick={onExport} disabled={busy} testId="vs-export-zip">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Building ZIP…</>
                     : <><Download className="w-4 h-4" /> Download campaign ZIP</>}
        </PrimaryBtn>
        <GhostBtn onClick={downloadManifest} testId="vs-export-meta-manifest">
          <FileJson className="w-4 h-4" /> Meta publish manifest (JSON)
        </GhostBtn>
        {!connected ? (
          <GhostBtn onClick={connectMeta} testId="vs-meta-connect">
            <Facebook className="w-4 h-4" /> Connect Meta
          </GhostBtn>
        ) : (
          <button onClick={publishMeta} disabled={publishing || posts.length === 0}
              data-testid="vs-meta-publish"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                          bg-gradient-to-tr from-blue-500 to-indigo-500
                          text-white font-medium shadow-lg shadow-blue-500/20
                          hover:shadow-blue-500/40 hover:-translate-y-0.5
                          transition disabled:opacity-40">
            {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</>
                             : <><Facebook className="w-4 h-4" /><Instagram className="w-4 h-4" /> Publish to Meta</>}
          </button>
        )}
        <button
          onClick={shipToSocialPulse}
          disabled={shipping || posts.length === 0}
          data-testid="vs-ship-socialpulse"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                      bg-gradient-to-tr from-fuchsia-500 via-pink-500 to-orange-500
                      text-white font-semibold shadow-lg shadow-fuchsia-500/20
                      hover:shadow-fuchsia-500/40 hover:-translate-y-0.5
                      transition disabled:opacity-40"
        >
          {shipping ? <><Loader2 className="w-4 h-4 animate-spin" /> Shipping…</>
                        : <><Rocket className="w-4 h-4" /> Ship to SocialPulse</>}
        </button>
      </div>

      {handoff && (
        <div
          data-testid="vs-socialpulse-receipt"
          className="mx-auto max-w-xl bg-fuchsia-500/5 border border-fuchsia-400/30 rounded-2xl p-4 text-sm"
        >
          <div className="flex items-center gap-2 font-semibold text-fuchsia-300">
            <Rocket className="w-4 h-4" />
            Handoff receipt
          </div>
          <div className="mt-2 text-xs text-slate-400 space-y-1 font-mono">
            <div>handoff_id: {handoff.handoff_id}</div>
            <div>creatives: {handoff.payload?.creatives?.length}</div>
            <div>ttl: {handoff.ttl_seconds}s</div>
            {handoff.ack ? (
              <>
                <div className="text-emerald-300">
                  socialpulse_campaign_id: {handoff.ack.socialpulse_campaign_id}
                </div>
                <div className="text-emerald-300">
                  report_id: {handoff.ack.report_id}
                </div>
              </>
            ) : (
              <div className="text-amber-300">ack pending</div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Every handoff is signed (HS256) and single-use. The receipt is written to the campaign audit log.
          </p>
        </div>
      )}
      {connected && (
        <div className="text-center text-[11px] text-slate-500 -mt-3">
          Connected as {metaStatus.pages?.[0]?.name || "your page"} · mode:{" "}
          <span className="font-mono">{metaStatus.kind}</span>
        </div>
      )}
      <div className="text-center text-[11px] text-slate-500 max-w-md mx-auto -mt-2">
        The Meta manifest packages everything the Facebook &amp; Instagram Graph
        API needs to publish these approved posts — plug in once your Meta
        Business App is reviewed.
      </div>
      <div className="space-y-4">
        {posts.map(p => (
          <div key={p.post_id} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex gap-4">
            <div className="w-32 h-32 bg-black/40 rounded-lg overflow-hidden flex-shrink-0 relative">
              {p.renders?.square?.url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.renders.square.url}`}
                       alt="" className="w-full h-full object-cover" />
              )}
              {p.renders?.square?.origin && (
                <div className="absolute bottom-1 left-1">
                  <RightsChip origin={p.renders.square.origin} />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="font-semibold">{p.headline}</div>
              <div className="text-sm text-slate-300 whitespace-pre-line">{p.caption}</div>
              <div className="text-xs text-fuchsia-300">{(p.hashtags || []).join(" ")}</div>
              <div className="flex gap-2 pt-2">
                {p.renders?.square?.url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${p.renders.square.url}`}
                      download data-testid={`vs-export-img-${p.post_id}`}
                      className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Image
                  </a>
                )}
                <button onClick={() => copy(p.caption, "Caption")}
                          data-testid={`vs-copy-caption-${p.post_id}`}
                          className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Caption
                </button>
                <button onClick={() => copy((p.hashtags || []).join(" "), "Hashtags")}
                          data-testid={`vs-copy-hashtags-${p.post_id}`}
                          className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Hashtags
                </button>
                {p.destination_url && (
                  <button onClick={() => copy(p.destination_url, "Link")}
                            data-testid={`vs-copy-link-${p.post_id}`}
                            className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Link
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="text-center py-10 text-slate-500 text-sm">
            No approved posts yet — go back and approve at least one.
          </div>
        )}
      </div>
      <div className="flex justify-start">
        <GhostBtn onClick={onBack}><ArrowLeft className="w-4 h-4" /> Back</GhostBtn>
      </div>
    </div>
  );
}
